import {useForm} from '@tanstack/react-form';
import {EvDatePickerField} from '../../../form/EvDatepickerField';
import {useFetchPositionFreezeMutation} from './use-fetch-position-freeze-mutation';
import {useState} from 'react';
import {EvButtonLink} from '../../../form/EvButtonLink';
import {EvDateFns} from '../../../components/ev-date-fns';
import {EvButton} from '../../../form/EvButton';
import {FetchPositionClean} from '../../../../envisia/workflow/fetch/box/fetch-box.model';
import {useAxiosError} from '../../../errorhandling/useAxiosError';
import {clsx} from 'clsx';
import {
  FetchPositionFreezeForm,
  FetchPositionFreezeFormState,
  FetchPositionFreezeFormStatus,
} from './fetch-position-freeze-form.models';
import {EvNumberInputField} from '../../../form/EvNumberInputField';
import {Money} from '../../../../common/money/Money';
import {NumberHelper} from '../../../../helper/number-helper';
import {FETCH_STATUS} from '../../fetch-status.model';

function priceFormFromFetchPosition(position: FetchPositionClean): FetchPositionFreezeForm {
  return {
    'date_frozen': position.date_frozen,
    'gold_price_frozen': NumberHelper.saveParseFloat(position.price) ?? 0,
  };
}

interface Props {
  position: FetchPositionClean;
  fetchStatus: number;
  hasTaskChangeGoldRight: boolean;
  update?: (() => void) | null | undefined;
}

export function FetchPositionFreezeForm(props: Props) {
  const currentView: (value: string | null | undefined) => FetchPositionFreezeFormStatus = (value) => {
    return (value ?? null) === null ? 'default' : 'frozen';
  };
  const [state, setState] = useState<FetchPositionFreezeFormState>({
    view: currentView(props.position.date_frozen),
  });
  const mutation = useFetchPositionFreezeMutation(props.position.id);
  const formError = useAxiosError(mutation.error);
  const form = useForm({
    defaultValues: priceFormFromFetchPosition(props.position),
    onSubmit: ({value, formApi}) => {
      mutation.mutate(
        {
          date_frozen: value.date_frozen,
          gold_price_frozen: state.view === 'formWithPrice' ? value.gold_price_frozen : null,
        },
        {
          onSuccess: position => {
            setState({view: currentView(position.object.date_frozen)});
            formApi.reset(priceFormFromFetchPosition(props.position));
            if (!!props.update) {
              props.update();
            }
          },
        });
    },
  });

  const showForm: () => void = () => {
    setState({...state, view: 'form'});
  };

  const manualGoldPrice: () => void = () => {
    setState({...state, view: 'formWithPrice'});
  };

  const resetGoldPrice: () => void = () => {
    if (!confirm('Goldpreis wirklich zurücksetzen?')) {
      return;
    }

    mutation.mutate(
      {date_frozen: null},
      {
        onSuccess: (position) => {
          setState({...state, view: currentView(position.object.date_frozen)});
          if (!!props.update) {
            props.update();
          }
        },
      });
  };

  switch (state.view) {
    case 'frozen':
      return <>
        <span title={
          `Datum zu dem der Goldpreis eingefroren wurde. Goldpreis: ${props.position.gold_price_frozen} € / g`
        }>
          <i className="fa fa-snowflake-o text-blue-400"/>&nbsp;<EvDateFns date={props.position.date_frozen}/>
        </span>&nbsp;
        <div className="inline-block pr-2 w-5">
          {
            props.fetchStatus < FETCH_STATUS.CLEAR ?
              <EvButtonLink title="Goldpreis Datum ändern" onClick={() => showForm()}>
                <i className="fa fa-pencil"/>
              </EvButtonLink> :
              null
          }
        </div>

        {
          (props.hasTaskChangeGoldRight && props.fetchStatus < FETCH_STATUS.CLEAR) &&
          <div className="inline-block pr-2 w-5">
            <EvButtonLink title="Manueller Goldpreis in € / LP setzen" onClick={() => manualGoldPrice()}>
              <i className="fa fa-euro text-storz-gold"/>
            </EvButtonLink>
          </div>
        }

        <div className="inline-block pr-2 w-5">
          {
            props.fetchStatus < FETCH_STATUS.DELIVERY ?
              <EvButtonLink title="Goldpreis zurücksetzen" onClick={() => resetGoldPrice()}>
                <i className="fa fa-repeat text-storz-red"/>
              </EvButtonLink> : null
          }
        </div>
      </>;
    case 'form':
    case 'formWithPrice':
      return <>
        <form onSubmit={async e => {
          e.preventDefault();
          await form.handleSubmit();
        }}>
          <div className="inline-block w-36 pr-2">
            <form.Field name="date_frozen">
              {(field) =>
                <EvDatePickerField id={`date_frozen_${props.position.id}`}
                                   title={`Goldpreis Datum${(state.view === 'formWithPrice') ? ' (exakt)' : ''}`}
                                   field={field}
                                   className={clsx(
                                     'text-right',
                                     {'red_alert': !!(formError?.raw?.response?.data ?? {})['obj.date_frozen']},
                                   )}/>
              }
            </form.Field>
          </div>
          {(state.view === 'formWithPrice') && <div className="inline-block w-32 pr-2">
            <form.Field name="gold_price_frozen">
              {(field) =>
                <EvNumberInputField id={`gold_price_frozen_${props.position.id}`}
                                    className="align-right"
                                    title="Goldpreis in € / LP"
                                    field={field}
                                    numberType="decimal"
                                    decimals={2}
                                    numberFormatFunction={Money.stringify}/>
              }
            </form.Field>
          </div>}
          <div className="inline-block w-52 pr-2">
            <EvButton type="submit" variant="success" size="sm">Goldpreis einfrieren</EvButton>
          </div>
        </form>
      </>;
    default:
      return <>
        <EvButtonLink title="Goldpreis einfrieren" onClick={() => showForm()}>
          <i className="fa fa-snowflake-o text-blue-400"/>
        </EvButtonLink>
        &nbsp;
        {
          (props.hasTaskChangeGoldRight && props.fetchStatus < FETCH_STATUS.CLEAR) &&
          <EvButtonLink title="Manueller Goldpreis in € / LP setzen" onClick={() => manualGoldPrice()}>
            <i className="fa fa-euro text-storz-gold"/>
          </EvButtonLink>
        }
      </>;
  }
}
